// packages
import { index } from 'd3-array';
import { json } from 'd3-fetch';
import { derived, writable } from 'svelte/store';

// local
import { getFullStateUrl } from '../constants';
import { indexById, indexByApId, indexByCode } from './commons';

// types
import type { Readable } from 'svelte/store';
import type { RaceFeed, RaceInterface, RaceLookup } from '../interfaces';

export async function createStateFeed(
  postal: string,
  seconds = 30
): Promise<Readable<RaceFeed>> {
  // build our URL
  const url = getFullStateUrl('2020-11-03', postal);

  // our timeout interval
  const interval = seconds * 1000;

  // we need to track the active timeout
  let timeoutId: number | undefined;

  // we need to keep track of the last run
  let lastUpdated: number;

  // set up our request function
  async function makeRequest() {
    console.debug('Making request:', url);
    const feed = (await json<RaceFeed>(url)) as RaceFeed;
    // const feed: RaceLookup = index(raw, indexById);

    lastUpdated = Date.now();
    return feed;
  }

  // get the initial data
  const feed = await makeRequest();

  // create our store
  const { subscribe } = writable(feed, function start(set) {
    async function run() {
      // make our request
      const feed = await makeRequest();

      // pass it along
      console.debug('Making update:', url);
      set(feed);

      timeoutId = setTimeout(run, interval);
    }

    const now = Date.now();

    if (now - lastUpdated < interval) {
      // we just had a run, so cool it
      timeoutId = setTimeout(run, interval);
    } else {
      // it's been a while, so get going!
      run();
    }

    return function stop() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
  });

  return { subscribe };
}

export function createRaceLookup(store: Readable<RaceFeed>) {
  return derived(store, ($feed) => index($feed, indexById));
}

export function createRaceStore(store: Readable<RaceLookup>, raceId: string) {
  return derived(store, ($lookup) => $lookup.get(raceId) as RaceInterface);
}

// export function createRaceLookup(store: Readable<>)

export function createCandidateLookup(store: Readable<RaceInterface>) {
  return derived(store, ($race) => index($race.candidates, indexByApId));
}

export function createSubunitLookup(store: Readable<RaceInterface>) {
  return derived(store, ($race) =>
    index(
      $race.subunits.filter((r) => r.type !== 'district'),
      indexByCode
    )
  );
}

export function createStateLookup(store: Readable<RaceFeed>) {
  return derived(store, ($feed) => index($feed, indexById));
}

export function createOfficeFeed(store: Readable<RaceFeed>, office: string) {
  return derived(store, ($feed) => {
    const feed = [];

    for (let idx = 0; idx < $feed.length; idx++) {
      const item = $feed[idx];

      if (item.office === office) {
        feed.push(item);
      }
    }

    return feed;
  });
}
